import { useCallback } from 'react';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCurrentUser } from '../../../providers/UserProvider';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import { SignatureDeleteCommand } from '../UndoRedo/SignatureDeleteCommand';
import { selectBlockContent } from '../grid/reduxStore/editorSlice';
import { rootStore } from '../grid/reduxStore/Store';
import type { SelectedSignatureType } from '../providers/SignaturesProvider';
import type { BlocksContentCollection } from '../grid/reduxStore/editorSlice';

export function useSignatureDeleteHandler() {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const { data } = useCurrentUser();
  const userId = data.id;
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    ({ sectionId, signatureId }: SelectedSignatureType, _blocksContent?: BlocksContentCollection): void => {
      if (!sectionId || !signatureId) return;

      const state = rootStore.getState();
      const blockContent = selectBlockContent(state, sectionId, signatureId);

      if (!blockContent || !blockContent.contentSignature) {
        console.warn('useSignatureDeleteHandler: signature block not found in Redux');
        return;
      }

      const signature = blockContent.contentSignature;
      const blockConfig = {
        id: signatureId,
        x: blockContent.blockConfig.x,
        y: blockContent.blockConfig.y,
        width: blockContent.blockConfig.width,
        height: blockContent.blockConfig.height,
        z: blockContent.blockConfig.z,
      };

      const command = new SignatureDeleteCommand(
        { dispatch, socketClient, userId, checkDocumentLockStatus },
        { sectionId, signature, blockConfig }
      );

      command.applyOptimistic();

      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      undoRedoManager.pushUndoRedoCommands(command);

      command.persistToSocket().then((success) => {
        if (!success) {
          console.error('useSignatureDeleteHandler: socket persist failed, state was rolled back');
          undoRedoManager.clearBlockCommands(signatureId);
        }
      });
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );
}
